import {
  publishTableLayoutResponseSchema,
  tableLayoutResponseSchema,
  type LeadCatalogResponse,
  type TableColumn,
  type TableLayoutResponse,
  type TableScope,
  type TableSort,
} from "@gioia365/contracts";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { apiRequest } from "../../api/http";
import type { Translate } from "../../i18n/locale";
import { tabKey, type TabContext } from "../../session/tab-context";
import {
  columnsToSave,
  deviceStorage,
  readDeviceLayout,
  resolveLayout,
  resolveTableView,
  sameColumns,
  tableScopeKey,
  writeDeviceLayout,
  type ColumnDef,
  type ResolvedLayout,
} from "./table-layout";

// Le letture e le scritture della vista di una tabella (piano M3 Task 6, D5): la vista dell'azienda e quella personale
// dal server (`GET /api/table-layouts/leads`, dal Task 7 anche quella di una Smart List), «Salva per me», «Usa la vista
// di {organization}» e «Salva per tutta {organization}». La chiave sta sotto `["scheda", tabKey, …]`, così il cambio di
// azienda la toglie; non porta la Vista Commerciale, perché la vista è sempre di chi guarda, mai del Commerciale
// osservato. La disposizione non salvata vive soltanto sul dispositivo (`useTableLayout`).

/** Il percorso della vista di un contesto: l'elenco Lead, oppure una Smart List (rotte del Task 7). */
function layoutPath(scope: TableScope): string {
  return scope.scopeType === "leads" ? "/api/table-layouts/leads" : `/api/table-layouts/smart-lists/${encodeURIComponent(scope.scopeId)}`;
}

export const tableLayoutKey = (tab: TabContext, scope: TableScope) => ["scheda", tabKey(tab), "table-layout", scope.scopeType, scope.scopeId] as const;

/** La vista dell'azienda, quella personale e se chi guarda può pubblicare (`GET /api/table-layouts/…`). */
export function tableLayoutQuery(tab: TabContext, scope: TableScope) {
  return queryOptions({
    queryKey: tableLayoutKey(tab, scope),
    queryFn: ({ signal }) => apiRequest("GET", layoutPath(scope), { schema: tableLayoutResponseSchema, tab, signal }),
    // Cambia soltanto quando qualcuno salva o pubblica: tornare all'elenco non la rilegge.
    staleTime: 5 * 60_000,
  });
}

/** «Salva per me»: la vista personale intera; la risposta è la vista aggiornata, che prende il posto di quella in cache. */
export function useSaveMyLayout(tab: TabContext, scope: TableScope) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ columns, sort }: { columns: TableColumn[]; sort?: TableSort | null }) =>
      apiRequest("PUT", `${layoutPath(scope)}/mine`, {
        schema: tableLayoutResponseSchema,
        body: { columns: columnsToSave(columns), ...(sort === undefined ? {} : { sort }) },
        tab,
      }),
    onSuccess: (layout) => queryClient.setQueryData(tableLayoutKey(tab, scope), layout),
  });
}

/** «Usa la vista di {organization}»: toglie la vista personale (`204`, anche se non c'era). */
export function useClearMyLayout(tab: TabContext, scope: TableScope) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest("DELETE", `${layoutPath(scope)}/mine`, { schema: z.undefined(), tab }),
    onSuccess: () =>
      queryClient.setQueryData<TableLayoutResponse>(tableLayoutKey(tab, scope), (layout) => (layout ? { ...layout, personal: null } : layout)),
  });
}

/**
 * «Salva per tutta {organization}»: una versione nuova della vista dell'azienda, che parte da `expectedVersion` (0 se
 * non ce n'è una). Dopo, e anche dopo un `409 version_conflict`, la vista si rilegge: le viste personali del contesto
 * si sono azzerate, oppure qualcun altro ha pubblicato nel frattempo.
 */
export function usePublishLayout(tab: TabContext, scope: TableScope) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { columns: TableColumn[]; sort?: TableSort | null; expectedVersion: number }) =>
      apiRequest("POST", `${layoutPath(scope)}/publish`, {
        schema: publishTableLayoutResponseSchema,
        body: { columns: columnsToSave(input.columns), ...(input.sort === undefined ? {} : { sort: input.sort }), expectedVersion: input.expectedVersion },
        tab,
      }),
    onSettled: () => queryClient.refetchQueries({ queryKey: tableLayoutKey(tab, scope), exact: true }),
  });
}

/** Lo stato di una lettura che la vista aspetta: in arrivo, fallita (senza dati), arrivata. */
export type LoadStatus = "pending" | "error" | "ready";

export type TableLayoutState = {
  /** Il catalogo delle colonne (con quelle dei campi dell'azienda ancora in arrivo o non arrivati). */
  catalog: ColumnDef[];
  /** La vista in uso e da dove viene. */
  resolved: ResolvedLayout;
  /** Le viste del server, quando sono arrivate. */
  server: TableLayoutResponse | undefined;
  /** Le viste del server: finché non sono «ready» non si sa quale vista è salvata, e non si salva. */
  layoutStatus: LoadStatus;
  /** I campi dell'azienda: finché non sono «ready» le loro colonne hanno la chiave per nome, e non si salva. */
  catalogStatus: LoadStatus;
  retryLayout: () => void;
  retryCatalog: () => void;
  /** Una disposizione nuova: resta sul dispositivo, o si toglie quando torna uguale alla vista salvata. */
  change: (columns: TableColumn[]) => void;
  /**
   * Tiene sul dispositivo proprio queste colonne, anche se sono uguali alla vista salvata: prima di una pubblicazione,
   * perché un `409` (con le viste personali azzerate da un collega) non le sostituisca con quelle appena rilette.
   */
  pin: (columns: readonly TableColumn[]) => void;
  /**
   * Dimentica la disposizione del dispositivo: per tornare alla vista salvata, oppure dopo un salvataggio (`saved`, le
   * colonne salvate), ma soltanto se nel frattempo non è cambiata ancora.
   */
  clearDevice: (saved?: readonly TableColumn[]) => void;
};

/** La lettura dei campi dell'azienda (`leadCatalogQuery`), come la usa la vista. */
export type CatalogRead = { data: LeadCatalogResponse | undefined; isError: boolean; refetch: () => unknown };

function statusOf(read: { data: unknown; isError: boolean }): LoadStatus {
  if (read.data !== undefined) return "ready";
  return read.isError ? "error" : "pending";
}

/**
 * La vista di una tabella per chi guarda (D5): le viste del server, la disposizione non salvata sul dispositivo
 * (`gioia365.vista.<tabKey>.<contesto>`) e il catalogo delle colonne. Ogni cambio si scrive sul dispositivo; quando la
 * disposizione torna uguale alla vista salvata (personale, dell'azienda o iniziale) la voce si toglie. Finché le viste
 * del server non sono arrivate la vista salvata non si conosce: un cambio resta sul dispositivo comunque. Finché i
 * campi dell'azienda non sono arrivati (o se la loro lettura fallisce) le loro colonne già nella vista restano, con la
 * chiave per nome: nessuna disposizione le perde in silenzio.
 */
export function useTableLayout(input: { tab: TabContext; scope: TableScope; t: Translate; catalog: CatalogRead }): TableLayoutState {
  const { tab, scope, t, catalog: catalogRead } = input;
  const layoutQuery = useQuery(tableLayoutQuery(tab, scope));
  const server = layoutQuery.data;
  const layoutStatus = statusOf(layoutQuery);
  const catalogStatus = statusOf(catalogRead);
  const catalog = catalogRead.data;
  const key = tabKey(tab);
  const scopeKey = tableScopeKey(scope);
  const storage = useMemo(() => deviceStorage(), []);
  // La disposizione del dispositivo per questa azienda e questo contesto; un'altra azienda o un altro contesto la rileggono.
  const owner = `${key}|${scopeKey}`;
  const [device, setDevice] = useState<{ owner: string; columns: TableColumn[] | null }>(() => ({ owner, columns: readDeviceLayout(storage, key, scopeKey) }));
  const deviceColumns = device.owner === owner ? device.columns : readDeviceLayout(storage, key, scopeKey);
  if (device.owner !== owner) setDevice({ owner, columns: deviceColumns });

  const company = server?.company?.columns ?? null;
  const personal = server?.personal?.columns ?? null;
  const view = useMemo(() => resolveTableView({ t, catalog, company, personal, device: deviceColumns }), [t, catalog, company, personal, deviceColumns]);
  const columns = view.columns;
  const resolved = view.resolved;
  // La disposizione dell'ultimo disegno, per chi la confronta dopo una richiesta (un salvataggio che torna).
  const latest = useRef(resolved.columns);
  useEffect(() => {
    latest.current = resolved.columns;
  });

  const keep = useCallback(
    (value: readonly TableColumn[] | null) => {
      writeDeviceLayout(storage, key, scopeKey, value);
      setDevice({ owner, columns: value === null ? null : [...value] });
    },
    [storage, key, scopeKey, owner],
  );

  const layoutReady = layoutStatus === "ready";
  const change = useCallback(
    (next: TableColumn[]) => {
      if (!layoutReady) {
        keep(next);
        return;
      }
      const saved = resolveLayout({ catalog: columns, company, personal, device: null }).columns;
      keep(sameColumns(next, saved) ? null : next);
    },
    [layoutReady, columns, company, personal, keep],
  );

  const clearDevice = useCallback(
    (saved?: readonly TableColumn[]) => {
      if (saved !== undefined && !sameColumns(latest.current, saved)) return;
      keep(null);
    },
    [keep],
  );

  const refetchLayout = layoutQuery.refetch;
  const refetchCatalog = catalogRead.refetch;
  const retryLayout = useCallback(() => void refetchLayout(), [refetchLayout]);
  const retryCatalog = useCallback(() => void refetchCatalog(), [refetchCatalog]);

  return { catalog: columns, resolved, server, layoutStatus, catalogStatus, retryLayout, retryCatalog, change, pin: keep, clearDevice };
}
